import { Component, Inject, OnInit } from '@angular/core';
import { SharedModule } from '../../../shared/shared.module';
import { MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA, MatLegacyDialog, MatLegacyDialogModule, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { Transaction } from '../../../../models/entities/transactions/transaction';
import { GenericDialogResult } from '../generic-mat-dialog/generic-dialog-result';
import { TransactionsService } from '../../../../services/accounting/transactions.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { MatDialogSizes } from '../../../../enums/mat-dialog-sizes.enum';
import { CoreUrlPipe } from '../../../../pipes/format/core-url.pipe';
import { MatExpansionModule } from '@angular/material/expansion';
import { PaymentAttempt } from '../../../../models/entities/payment-attempt';
import { MatLegacyTableModule } from '@angular/material/legacy-table';
import { PayableDetailsComponent } from '../../accounting/transactions/payable-details/payable-details.component';
import { JwtLegFiClaims } from '../../../../services/auth/jwt-legfi-claims.model';
import { LegFiJwtService } from '../../../../services/auth/legfi-jwt.service';
import { LegfiDatePipe } from 'app/pipes/format/legfi-date.pipe';
import { LegfiCurrencyPipe } from 'app/pipes/format/legfi-currency.pipe';
import { LegfiBalancePipe } from '../../../../pipes/format/legfi-balance.pipe';
import { WhereInPipe } from '../../../../pipes/collection/where-in.pipe';
import { AsPipe } from '../../../../pipes/format/as.pipe';
import { SumPipe } from '../../../../pipes/collection/sum.pipe';
import { GroupByPipe } from '../../../../pipes/collection/group-by.pipe';
import { SuperStarComponent } from '../super-star/super-star.component';
import { LedgerEntriesService } from '../../../../services/accounting/ledger-entries.service';
import { GrowlerService } from '../../../shared/growler/growler.service';
import { ConfirmActionDialogComponent, ConfirmActionDialogInput, ConfirmActionDialogResult } from '../confirm-action-dialog/confirm-action-dialog.component';
import { getChildDisplayAmount as computeChildDisplayAmount } from '../../../../functions/transaction-display';

export interface TransactionDetailsDialogInput
{
    organizationId?: number;
    transaction?: Transaction;
    transactionId?: number;
}

@UntilDestroy()
@Component({
    selector: 'app-transaction-details-dialog',
    standalone: true,
    templateUrl: './transaction-details-dialog.component.html',
    imports: [
        MatLegacyDialogModule,
        SharedModule,
        CoreUrlPipe,
        MatExpansionModule,
        MatLegacyTableModule,
        PayableDetailsComponent,
        LegfiDatePipe,
        LegfiCurrencyPipe,
        LegfiBalancePipe,
        WhereInPipe,
        AsPipe,
        SumPipe,
        GroupByPipe,
        SuperStarComponent,
    ],
    styleUrls: ['./transaction-details-dialog.component.scss'],
})
export class TransactionDetailsDialogComponent implements OnInit
{
    PaymentAttempt!: PaymentAttempt;
    loading = true;
    jwt: JwtLegFiClaims;
    hasAccountingV2 = false;

    constructor(
            private _dialogRef: MatDialogRef<TransactionDetailsDialogComponent>,
            private _transactionsService: TransactionsService,
            private _ledgerEntriesService: LedgerEntriesService,
            private _growler: GrowlerService,
            private _dialog: MatLegacyDialog,
            @Inject(MAT_DIALOG_DATA) public data: TransactionDetailsDialogInput,
    ) {
    }

    ngOnInit() {
        this.jwt = LegFiJwtService.read();
        this.hasAccountingV2 = LegFiJwtService.featureEnabled('accounting-v2');
        this._dialogRef.updateSize(MatDialogSizes.MD);
        if (this.data.transactionId && !this.data.transaction) {
            this.getTransactionDetails();
        } else if (this.data.transaction) {
            this.loading = false;
        }
    }

    getTransactionDetails() {
        this._transactionsService.getTransaction(this.data.organizationId, this.data.transactionId).pipe(untilDestroyed(this)).subscribe({
            next: (transaction: Transaction) => {
                this.loading = false;
                this.data.transaction = transaction;
            },
            error: () => {

            },
        });
    }

    /**
     * Returns the display amount for child transactions with correct sign for color coding.
     * Delegates to shared utility function for consistent behavior across components.
     */
    getChildDisplayAmount(child: Transaction): number {
        return computeChildDisplayAmount(child);
    }

    closeDialog() {
        this._dialogRef.close(<GenericDialogResult>{success: false});
    }

    deleteLedgerBatch(batchId: string) {
        const data: ConfirmActionDialogInput = {
            title: 'Delete Ledger Batch?',
            content: 'This action is outside the regular flow of entries and should only be used to fix irreversible issues.',
            confirmButton: 'Delete Batch'
        };
        this._dialog.open(ConfirmActionDialogComponent, {
            data
        }).afterClosed().subscribe((result: ConfirmActionDialogResult) => {
            if(result?.success) {
                this._ledgerEntriesService.deleteBatch(this.jwt.orgId, batchId)
                        .subscribe({
                            next: () => {
                                this._growler.success('Success', 'Ledger batch deleted successfully.');
                                this.getTransactionDetails();
                            }
                        });
            }
        });

    }
}
